import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassmorphicCard } from '../styles/sharedStyles';

const EmailSettings = ({ apiBaseUrl, token }) => {
  const [config, setConfig] = useState({
    smtp_server: '',
    smtp_port: 465,
    sender_email: '',
    sender_password: '',
    use_ssl: true,
    recipient_email: '',
    notify_login_success: true,
    notify_login_failure: true,
    notify_ip_ban: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch email configuration on mount
  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/email/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig({
        smtp_server: response.data.smtp_server || '',
        smtp_port: response.data.smtp_port || 465,
        sender_email: response.data.sender_email || '',
        sender_password: '', // Don't show password for security
        use_ssl: response.data.use_ssl !== undefined ? response.data.use_ssl : true,
        recipient_email: response.data.recipient_email || '',
        notify_login_success:
          response.data.notify_login_success !== undefined
            ? response.data.notify_login_success
            : true,
        notify_login_failure:
          response.data.notify_login_failure !== undefined
            ? response.data.notify_login_failure
            : true,
        notify_ip_ban:
          response.data.notify_ip_ban !== undefined ? response.data.notify_ip_ban : true,
      });
      setErrorMessage('');
    } catch (err) {
      console.error('Error fetching email config:', err);
      setErrorMessage('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handlePortChange = (e) => {
    const port = e.target.value ? parseInt(e.target.value, 10) : 465;
    setConfig(prev => ({
      ...prev,
      smtp_port: port,
    }));
  };

  const handleSaveConfig = async () => {
    if (!config.smtp_server || !config.smtp_port || !config.sender_email || 
        !config.sender_password || !config.recipient_email) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await axios.post(
        `${apiBaseUrl}/api/email/config`,
        config,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage('✅ Email configuration saved successfully');
        // Clear password after save for security
        setConfig(prev => ({
          ...prev,
          sender_password: '',
        }));
      } else {
        setErrorMessage(response.data.message || 'Failed to save email configuration');
      }
    } catch (err) {
      console.error('Error saving email config:', err);
      setErrorMessage(err.response?.data?.message || 'Error saving email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!config.sender_email || !config.smtp_server) {
      setErrorMessage('Please configure email settings first');
      return;
    }

    try {
      setTestingEmail(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await axios.post(
        `${apiBaseUrl}/api/email/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage('✅ Test email sent successfully');
      } else {
        setErrorMessage(response.data.message || 'Failed to send test email');
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      setErrorMessage(err.response?.data?.message || 'Error sending test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
      {/* Messages */}
      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage('')}
          sx={{ mb: 2, borderRadius: '12px' }}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage('')}
          sx={{ mb: 2, borderRadius: '12px' }}
        >
          {errorMessage}
        </Alert>
      )}

      <GlassmorphicCard
        sx={{
          borderRadius: '16px',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: '#00d4ff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            📧 Email Notifications Configuration
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
          >
            Configure email notifications to receive alerts about login attempts (successful and failed).
          </Typography>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* SMTP Server Configuration */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: '#00d4ff',
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
            }}
          >
            SMTP Server Configuration
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Server"
                name="smtp_server"
                value={config.smtp_server}
                onChange={handleInputChange}
                placeholder="smtp.mail.ovh.net"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    opacity: 1,
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Port"
                name="smtp_port"
                type="number"
                value={config.smtp_port}
                onChange={handlePortChange}
                placeholder="465"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    opacity: 1,
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.use_ssl}
                    onChange={handleInputChange}
                    name="use_ssl"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#00d4ff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: 'rgba(0, 212, 255, 0.3)',
                        },
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                    Use SSL/TLS
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Authentication */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: '#00d4ff',
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
            }}
          >
            Authentication
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sender Email"
                name="sender_email"
                type="email"
                value={config.sender_email}
                onChange={handleInputChange}
                placeholder="notifications@raidnet.pl"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    opacity: 1,
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                name="sender_password"
                type="password"
                value={config.sender_password}
                onChange={handleInputChange}
                placeholder="Your password"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    opacity: 1,
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Recipient */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: '#00d4ff',
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
            }}
          >
            Email Recipient
          </Typography>

          <TextField
            fullWidth
            label="Recipient Email Address"
            name="recipient_email"
            type="email"
            value={config.recipient_email}
            onChange={handleInputChange}
            placeholder="your.email@example.com"
            variant="outlined"
            size="small"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00d4ff',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.3)',
                opacity: 1,
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: '#00d4ff',
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
            }}
          >
            Alert Types
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
          >
            Choose which types of alerts should be delivered to your inbox. Disabled alerts will not trigger emails.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.notify_login_success}
                    onChange={handleInputChange}
                    name="notify_login_success"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#00d4ff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: 'rgba(0, 212, 255, 0.3)',
                        },
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                    Successful logins
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.notify_login_failure}
                    onChange={handleInputChange}
                    name="notify_login_failure"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#00d4ff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: 'rgba(0, 212, 255, 0.3)',
                        },
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                    Failed logins
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.notify_ip_ban}
                    onChange={handleInputChange}
                    name="notify_ip_ban"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#00d4ff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: 'rgba(0, 212, 255, 0.3)',
                        },
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                    IP ban alerts
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleSaveConfig}
              disabled={saving || testingEmail}
              sx={{
                backgroundColor: (theme) => theme.palette.secondary.main,
                color: (theme) => theme.palette.secondary.contrastText,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.secondary.main,
                  filter: 'brightness(1.1)',
                  boxShadow: (theme) => `0 0 20px ${theme.palette.secondary.main}66`,
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ mr: 1, color: '#000' }} />
              ) : (
                <CheckIcon sx={{ mr: 1 }} />
              )}
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleSendTestEmail}
              disabled={saving || testingEmail}
              sx={{
                borderColor: '#00d4ff',
                color: '#00d4ff',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  borderColor: '#00e4ff',
                  backgroundColor: 'rgba(0, 212, 255, 0.1)',
                },
                '&:disabled': {
                  opacity: 0.6,
                  borderColor: 'rgba(0, 212, 255, 0.5)',
                  color: 'rgba(0, 212, 255, 0.5)',
                },
              }}
            >
              {testingEmail ? (
                <CircularProgress size={20} sx={{ mr: 1, color: '#00d4ff' }} />
              ) : (
                <SendIcon sx={{ mr: 1 }} />
              )}
              {testingEmail ? 'Sending...' : 'Send Test Email'}
            </Button>
          </Box>

          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 3, color: 'rgba(255, 255, 255, 0.5)' }}
          >
            💡 Tip: Send a test email to verify your configuration is working correctly before enabling notifications.
          </Typography>
        </CardContent>
      </GlassmorphicCard>
    </Box>
  );
};

export default EmailSettings;
